import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
    useReducer,
} from "react";
import moment from 'moment';
import { useStreamContext } from './StreamContext';
import { useUserContext } from './UserContext';
import { getHallsByDate, mappedEpisodes } from '../helpers/schedule-helper';
import * as CONSTANTS from '../constants/common';
import {VIEW_TYPE_PLATFORM, VIEW_TYPE_WIDGET} from '../constants/view-types';
import { PAGES_VIEW_ALL, PAGES_VIEW_GRID, PAGES_VIEW_LINES, TABS_TYPE } from '../constants/page-settings/schedule';
import { generateTimeIntervals } from '../helpers/times';

export const ScheduleContext = createContext(null);

export const useScheduleContext = () => useContext(ScheduleContext);

export const ScheduleContextProvider = ({
    event,
    page,
    viewType = VIEW_TYPE_PLATFORM,
    pdfSettings = null,
    widgetSettings = null,
    children,
}) => {
    const isWidget = viewType === VIEW_TYPE_WIDGET;

    const userContext = useUserContext();
    const streamContext = useStreamContext();
    const [episodes, setEpisodes] = useState([]);
    const [schedule, setSchedule] = useState([]);
    const [scheduleTimeLine, setScheduleTimeLine] = useState([]);
    const [scheduleHallsByDate, setScheduleHallsByDate] = useState(null);
    const [scheduleFilters, updateScheduleFilters] = useReducer(
        (prev, next) => {
            return { ...prev, ...next };
        },
        {
            scheduleDates: [],
            scheduleView: page?.settings?.view === PAGES_VIEW_ALL ? PAGES_VIEW_LINES : page?.settings?.view,
            episodesType: CONSTANTS.TYPE_HYBRID,
            date: null,
            searchQuery: '',
            selectedTags: [],
            selectedHall: null,
            suggestionOrganization: '',
        }
    );
    const daysView = isWidget ? widgetSettings?.daysView : page.settings.daysView;

    // Устанавливаем тольке те залы, у которых на выбранную дату есть эпизоды
    useEffect(() => {
        if (daysView === TABS_TYPE || scheduleFilters.scheduleView === PAGES_VIEW_GRID) {
            const halls = getHallsByDate(streamContext.streams, scheduleFilters.date);
            setScheduleHallsByDate(halls);
            setEpisodes(mappedEpisodes(halls));
        }
    }, [daysView, scheduleFilters, streamContext.streams]);

    useEffect(() => {
        let episodes = mappedEpisodes(streamContext.streams);
        episodes = episodes.filter((episode) => {
            /**
             * Если у эпизода значение participationType hybrid то отображаем эти эпизоды для всеx,
             * при условии что они не скрыты.
             */
            if (viewType === VIEW_TYPE_WIDGET || !episode.participationType || episode.participationType === CONSTANTS.TYPE_HYBRID) {
                return !episode.hidden;
            }

            /**
             * Если эпизоды не скрыты, participationType не hybrid
             * и userParticipationType равно participationType эпизода
             */
            return !episode.hidden && episode.participationType === userContext.participationType;
        });
        setEpisodes(episodes);
    }, [streamContext.streams, scheduleFilters]);

    const hasManyHalls = useMemo(() => {
        return streamContext.streams?.length > 1;
    }, [streamContext.streams]);

    function handleSetScheduleTimeLine(episodes) {
        const intervals = isWidget ? +widgetSettings?.intervals : +page.settings.intervals;

        if (intervals <= 0) return [];

        let episodesTimeLine = episodes
            .map(({timeStart}) => timeStart)
            .reduce((acc, timeStart) => {
                const transformedTimeStart = moment.unix(timeStart).startOf('hour').unix();
                const hasTime = acc.some((time) => time === transformedTimeStart);

                if (!hasTime) {
                    acc = [...acc, transformedTimeStart];
                }

                return acc;
            }, []);

        if (intervals > 0) {
            episodesTimeLine = generateTimeIntervals(
                episodesTimeLine[0],
                episodesTimeLine[episodesTimeLine.length - 1],
                intervals
            );
        }

        setScheduleTimeLine(episodesTimeLine);
    }

    const context = {
        event,
        page,
        episodes,
        setEpisodes,
        schedule,
        setSchedule,
        hasManyHalls,
        scheduleFilters,
        updateScheduleFilters,
        scheduleTimeLine,
        handleSetScheduleTimeLine,
        widgetSettings,
        scheduleHallsByDate,
        isWidget,
        pdfSettings,
    };

    return (
        <ScheduleContext.Provider value={context}>
            {children}
        </ScheduleContext.Provider>
    );
}
